<template>
  <div class="help">
    <!-- Hero -->
    <section class="help__hero">
      <div class="help__hero-logo">
        <img :src="$brand.logo" :alt="$brand.name" />
      </div>
      <div class="help__hero-text">
        <span class="help__hero-eyebrow">{{ $brand.name }}</span>
        <h1 class="help__hero-title">{{ fill(content.title) }}</h1>
        <p class="help__hero-sub">{{ fill(content.subtitle) }}</p>
      </div>
    </section>

    <!-- Sections -->
    <div class="help__grid">
      <OmxCard
          v-for="(section, i) in content.sections"
          :key="i"
          class="help__card"
      >
        <template #title>
          <span class="help__sec-head">
            <span class="help__sec-icon" :class="'is-' + (section.tone || 'brand')">
              <b-icon :icon="section.icon" size="is-small" />
            </span>
            <span class="help__sec-title">{{ fill(section.title) }}</span>
          </span>
        </template>

        <p v-if="section.body" class="help__lead">{{ fill(section.body) }}</p>

        <!-- numbered stepper -->
        <ol v-if="section.layout === 'steps'" class="help__steps">
          <li v-for="(point, j) in section.points" :key="j">
            <span class="help__step-num">{{ j + 1 }}</span>
            <span class="help__step-text">{{ fill(point) }}</span>
          </li>
        </ol>

        <!-- checklist -->
        <ul v-else-if="section.layout === 'checks'" class="help__checks">
          <li v-for="(point, j) in section.points" :key="j">
            <b-icon icon="check-circle" size="is-small" class="help__check-ico" />
            <span>{{ fill(point) }}</span>
          </li>
        </ul>

        <!-- default bulleted list -->
        <ul v-else class="help__list">
          <li v-for="(point, j) in section.points" :key="j">{{ fill(point) }}</li>
        </ul>
      </OmxCard>
    </div>
  </div>
</template>

<script>
import OmxCard from "@/components/OmxCard.vue";
import helpContent from "@/content/help";

export default {
  name: "Help",
  components: { OmxCard },
  computed: {
    content() {
      return helpContent[this.$i18n.locale] || helpContent.en;
    }
  },
  methods: {
    // Substitute the {brand} token with the active white-label brand name.
    fill(str) {
      return (str || "").replace(/\{brand\}/g, this.$brand.name);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.help {
  max-width: 920px;
}

/* ---- Hero ---- */
.help__hero {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 26px 30px;
  border-radius: $omx-radius-lg;
  background: linear-gradient(135deg, $omx-brand-700, $omx-brand-500);
  color: #fff;
  box-shadow: $omx-shadow-lg;
  margin-bottom: 20px;
}
.help__hero-logo {
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, .16);
  display: flex;
  align-items: center;
  justify-content: center;
  img { width: 40px; height: 40px; display: block; }
}
.help__hero-text { min-width: 0; }
.help__hero-eyebrow {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  opacity: .85;
  margin-bottom: 4px;
}
.help__hero-title {
  font-size: 25px;
  line-height: 1.2;
  font-weight: 800;
  color: #fff;
  margin: 0 0 6px;
}
.help__hero-sub {
  font-size: 14.5px;
  line-height: 1.5;
  margin: 0;
  opacity: .94;
  max-width: 68ch;
}

/* ---- Sections ---- */
.help__grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.help__sec-head {
  display: inline-flex;
  align-items: center;
  gap: 11px;
}
.help__sec-icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  &.is-brand  { background: $omx-brand-100;   color: $omx-brand-600; }
  &.is-green  { background: $omx-success-100; color: $omx-success-600; }
  &.is-warm   { background: $omx-warn-100;    color: $omx-warn-600; }
  &.is-purple { background: #efe9fb;          color: #6f3ed4; }
}
.help__sec-title {
  font-size: 16px;
  font-weight: 700;
  color: $omx-ink-900;
}
.help__lead {
  color: $omx-ink-700;
  line-height: 1.6;
  margin: 0;
}

/* default bullets */
.help__list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
}
.help__list li {
  position: relative;
  padding: 5px 0 5px 20px;
  color: $omx-ink-700;
  line-height: 1.55;
  &::before {
    content: "";
    position: absolute;
    left: 3px;
    top: 12px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $omx-brand-500;
  }
}

/* numbered stepper */
.help__steps {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
}
.help__steps li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid $omx-ink-50;
  &:last-child { border-bottom: none; }
}
.help__step-num {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: $omx-brand-600;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.help__step-text {
  color: $omx-ink-700;
  line-height: 1.55;
  padding-top: 3px;
}

/* checklist */
.help__checks {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
}
.help__checks li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 5px 0;
  color: $omx-ink-700;
  line-height: 1.55;
}
.help__check-ico {
  color: $omx-success-600;
  flex: 0 0 auto;
  margin-top: 1px;
}

@media (max-width: 600px) {
  .help__hero { flex-direction: column; align-items: flex-start; gap: 14px; padding: 22px; }
  .help__hero-title { font-size: 22px; }
}
</style>
